import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, NavigationExtras, Params, Route, Router, RouterStateSnapshot, Routes, UrlTree } from "@angular/router";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { filter, switchMap, map } from "rxjs/operators";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import { BpViewService} from '@mydocma/co/src/lib/common';
import { Project } from "@mydocma/project";
import { PersonalizationService } from "@mydocma/core";

@UntilDestroy()
@Injectable()
export class BpRoutingService  implements OnDestroy {

    private routesMap = new Map<string, Route>();
    private moduleActivatedRoute: ActivatedRoute;
    private moduleActivatedRouteSubject = new BehaviorSubject<{ route: ActivatedRoute, params: Params }>(null);
    private project: Project;

    public moduleActivatedRoute$ = this.moduleActivatedRouteSubject.asObservable();

    constructor(
        private readonly viewService: BpViewService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly zone: NgZone,
        private readonly personalizationService: PersonalizationService,
    ) {

        this.router.events
            .pipe(
                untilDestroyed(this),
                filter(e => (e as any)?.routerEvent instanceof NavigationEnd),
                switchMap(e => this.viewService.currentProject$.pipe(filter(Boolean)))
            )
            .subscribe(p => {
                this.moduleActivatedRoute = this.getModuleRoute(this.activatedRoute) as ActivatedRoute;
                if (this.moduleActivatedRoute) {
                    this.routesMap.clear();
                    this.addRoutesToMap([this.moduleActivatedRoute.routeConfig]);
                    this.onProjectChanged(p);
                    this.project = p;
                    const routeParams = this.getRouteParams(this.activatedRoute)
                    if (p && routeParams?.projectId &&
                      (routeParams.projectId === ':projectId' ) ) {
                      const path = this.getRoutePath(this.moduleActivatedRoute?.snapshot);
                      this.navigateTo(path,p)
                      return;
                    }
                    this.moduleActivatedRouteSubject.next({ route: this.moduleActivatedRoute, params: routeParams });
                } else {
                    this.moduleActivatedRouteSubject.next(null);
                }
            });

        this.moduleActivatedRouteSubject
            .pipe(
                filter(Boolean),
                switchMap(mr => this.viewService.currentProject$.pipe(filter(Boolean),map(p => ({p,mr})))),
                untilDestroyed(this)
            ).subscribe( ({p,mr}) => {
               const path = this.getRoutePath(this.moduleActivatedRouteSubject.value?.route.snapshot);
               if (!path || path === '/' || path=== '/projects') {
                this.navigateTo('/projects/:projectId/plans');
                return;
               }
            });

    }
    onProjectChanged(p: Project) {
        if(!p) this.navigateTo('/projects');
        if(p) this.personalizationService.setValue('CO.LastProject', new Project({id: p.id, customerId: p.customerId}));
        if ( this.project && this.project.id !== p.id ) {
            const url = this.router.url.replace(this.project.id, p.id)
            this.navigateTo(url);
        }
    }

    public get moduleRoute(): ActivatedRoute {
        return this.moduleActivatedRouteSubject.value.route;
    }


    ngOnDestroy(): void {
    }

    private getRouteParams(route: ActivatedRoute, params: Params = {}): Params {
        let newParams = { ...params, ...route.snapshot.params };
        if (route.firstChild) {
            newParams = this.getRouteParams(route.firstChild, newParams);
        }
        return newParams;

    }
    private addRoutesToMap(routes: Routes, parentPath: string = '') {
        if (!routes || routes.length === 0) {
            return;
        }
        routes.forEach(route => {
            const path = (parentPath + '/' + route.path).replace('//', '/').toLowerCase();
            this.routesMap.set(path, route);
            this.addRoutesToMap(route.children, path);
        });
    }

    private getModuleRoute(route: ActivatedRouteSnapshot | ActivatedRoute): ActivatedRouteSnapshot | ActivatedRoute {
        const snapshot = route instanceof ActivatedRoute ? route.snapshot : route;
        if (snapshot.data && snapshot.data.bpRoot === true) {
            return route;
        }
        for (const childRoute of route.children) {
            const mr = this.getModuleRoute(childRoute);
            if (mr) {
                return mr;
            }
        }
        return null;
    }

    public getRoutePath(route: ActivatedRouteSnapshot): string {
        let path = route.routeConfig.path;
        if (route.children && route.children.length === 1) {
            const cp = this.getRoutePath(route.children[0]);
            if (cp) {
                path = path + '/' + cp;
            }
        }
        return path;
    }



    private navigateTo(path: string, project: Project = null) {
        let u = path;
        if (project && project.id) {
            u = u.replace(':projectId', project.id);
        }
        this.navigate([u], { relativeTo: this.moduleActivatedRoute });
    }

    public navigate(commands: any[], extras?: NavigationExtras) {
        this.zone.run(() => {
            if(extras) {
                this.router.navigate(commands, { relativeTo: this.moduleActivatedRoute, queryParams: extras?.queryParams });
            } else {
                this.router.navigate(commands, { relativeTo: this.moduleActivatedRoute });
            }
        });
    }

    public getRouteByPath(path: string): Route {
        return this.routesMap.get(path.toLowerCase());
    }

    public canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot):
        boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {

        return of(true);

    }

}
